import { useEffect, useState } from "react";
import type { PageInfo } from "../types";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "./ui/alert-dialog";
import { useI18n } from "../i18n";

export interface DeletePageDialogProps {
  /** The page to delete; null — closed. */
  readonly page: PageInfo | null;
  /** All pages (the target list is every other one). */
  readonly pages: readonly PageInfo[];
  /** The players' map now: deleting it needs a place for them. */
  readonly activePageId: string;
  readonly hasChildren: boolean;
  readonly onConfirm: (pageId: string, movePlayersTo?: string) => void;
  readonly onClose: () => void;
}

/**
 * «Delete the page?» of the page list. The players' map is deleted only with
 * the map they go to, chosen here explicitly (no default: the first map of
 * the list may be a prepared secret one) — the server refuses without it.
 * Whether the page is the players' is read live: another GM may show it
 * while the dialog is open. Test ids are those of ConfirmDialog (the dialog it replaces).
 */
export default function DeletePageDialog({
  page,
  pages,
  activePageId,
  hasChildren,
  onConfirm,
  onClose,
}: DeletePageDialogProps) {
  const { t } = useI18n();
  const [target, setTarget] = useState("");
  useEffect(() => setTarget(""), [page?.id]);

  const playersHere = page !== null && page.id === activePageId;
  const targets = page === null ? [] : pages.filter((p) => p.id !== page.id);
  const targetOk = targets.some((p) => p.id === target);
  const canConfirm = page !== null && (!playersHere || targetOk);

  return (
    <AlertDialog open={page !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("pages.deleteConfirmTitle")}</AlertDialogTitle>
          <AlertDialogDescription>
            {hasChildren ? t("pages.deleteConfirmDescWithChildren") : t("pages.deleteConfirmDesc")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {playersHere && (
          <label className="flex flex-col gap-1 text-ui" data-testid="page-delete-players">
            <span>{t("pages.deletePlayersHere")}</span>
            <select
              data-testid="page-delete-target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 text-ui"
            >
              <option value="">{t("pages.deleteChooseTarget")}</option>
              {targets.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose} data-testid="confirm-dialog-cancel">
            {t("confirmDialog.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-dialog-confirm"
            disabled={!canConfirm}
            onClick={() => {
              if (page === null || !canConfirm) return;
              onConfirm(page.id, playersHere ? target : undefined);
              onClose();
            }}
          >
            {t("common.delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
